import { normalizeUrlTemplatePlaceholders, stripAllEmptyQueryParams } from './substituteUrlTemplate'

/** Whitelisted `[[name]]` placeholders for CTA URL templates (v1). */
export const CTA_URL_PLACEHOLDER_IDS = [
  'tenantId',
  'playerId',
  'playerTitle',
  'venueId',
  'tenantHandle',
  'playlistUrn',
  'frameId',
  'contentSourcePlaylistUrn',
  'scheduleRootUrn'
] as const

export type TCtaUrlPlaceholderId = (typeof CTA_URL_PLACEHOLDER_IDS)[number]

const CTA_PLACEHOLDER_ID_SET = new Set<string>(CTA_URL_PLACEHOLDER_IDS)

/** Fixed context for API validation and app preview (not real device ids). */
export const CTA_URL_SAMPLE_INTERPOLATION_CONTEXT: ICtaUrlInterpolationContext = {
  tenantId: 'sample-tenant-id',
  playerId: 'sample-player-id',
  playerTitle: 'Sample Screen',
  venueId: 'sample-venue-id',
  tenantHandle: 'sample-handle',
  playlistUrn: 'urn:lucimark:playlist:sample',
  frameId: 'sample-frame-id',
  contentSourcePlaylistUrn: 'urn:lucimark:playlist:embedded-sample',
  scheduleRootUrn: 'urn:lucimark:campaign:sample'
}

export interface ICtaUrlInterpolationContext {
  tenantId: string
  playerId: string
  playerTitle?: string
  venueId?: string | null
  tenantHandle?: string | null
  playlistUrn?: string
  frameId?: string
  contentSourcePlaylistUrn?: string | null
  scheduleRootUrn?: string | null
}

export function getCtaUrlPlaceholderKeys(template: string): Set<string> {
  const keys = new Set<string>()
  const normalized = normalizeUrlTemplatePlaceholders(template)
  normalized.replace(/\[\[(\w+)\]\]/g, (_match, key: string) => {
    keys.add(key)
    return ''
  })
  return keys
}

export function findUnknownCtaUrlPlaceholderKeys(template: string): string[] {
  const unknown: string[] = []
  for (const key of getCtaUrlPlaceholderKeys(template)) {
    if (!CTA_PLACEHOLDER_ID_SET.has(key)) {
      unknown.push(key)
    }
  }
  return unknown
}

function contextValueForKey(
  key: TCtaUrlPlaceholderId,
  context: ICtaUrlInterpolationContext
): string {
  switch (key) {
    case 'tenantId':
      return context.tenantId
    case 'playerId':
      return context.playerId
    case 'playerTitle':
      return context.playerTitle ?? ''
    case 'venueId':
      return context.venueId ?? ''
    case 'tenantHandle':
      return context.tenantHandle ?? ''
    case 'playlistUrn':
      return context.playlistUrn ?? ''
    case 'frameId':
      return context.frameId ?? ''
    case 'contentSourcePlaylistUrn':
      return context.contentSourcePlaylistUrn ?? ''
    case 'scheduleRootUrn':
      return context.scheduleRootUrn ?? ''
    default: {
      const _exhaustive: never = key
      return _exhaustive
    }
  }
}

/**
 * Substitute whitelisted `[[key]]` placeholders in a CTA URL template.
 * Unknown keys are left unchanged (API should reject them on save).
 */
export function substituteCtaUrlTemplate(
  template: string,
  context: ICtaUrlInterpolationContext
): string {
  const normalized = normalizeUrlTemplatePlaceholders(template)
  const substituted = normalized.replace(/\[\[(\w+)\]\]/g, (_match, key: string) => {
    if (!CTA_PLACEHOLDER_ID_SET.has(key)) {
      return `[[${key}]]`
    }
    const val = contextValueForKey(key as TCtaUrlPlaceholderId, context)
    return encodeURIComponent(val)
  })
  return stripAllEmptyQueryParams(substituted)
}

export const CTA_URL_VALIDATION_ERROR_CODES = [
  'required',
  'tooLong',
  'schemeNotAllowed',
  'unknownPlaceholders',
  'mustStartWithHttps',
  'invalidAfterSubstitution',
  'mustUseHttpsAfterSubstitution',
  'mustNotIncludeCredentials'
] as const

export type TCtaUrlValidationErrorCode = typeof CTA_URL_VALIDATION_ERROR_CODES[number]

export function ctaUrlValidationMessage(
  code: TCtaUrlValidationErrorCode,
  details?: { unknownPlaceholders?: string[] }
): string {
  switch (code) {
    case 'required':
      return 'URL is required'
    case 'tooLong':
      return 'URL must be at most 2048 characters'
    case 'schemeNotAllowed':
      return 'URL scheme is not allowed'
    case 'unknownPlaceholders':
      return `Unknown placeholders: ${(details?.unknownPlaceholders ?? []).join(', ')}`
    case 'mustStartWithHttps':
      return 'URL must start with https://'
    case 'invalidAfterSubstitution':
      return 'CTA URL is not a valid URL after substituting placeholders'
    case 'mustUseHttpsAfterSubstitution':
      return 'CTA URL must use https after substituting placeholders'
    case 'mustNotIncludeCredentials':
      return 'CTA URL must not include credentials'
    default: {
      const _exhaustive: never = code
      return _exhaustive
    }
  }
}

export function assertValidCtaUrlAfterSubstitution(url: string): void {
  let parsed: URL
  try {
    parsed = new URL(url)
  }
  catch {
    throw new Error(ctaUrlValidationMessage('invalidAfterSubstitution'))
  }
  if (parsed.protocol !== 'https:') {
    throw new Error(ctaUrlValidationMessage('mustUseHttpsAfterSubstitution'))
  }
  if (parsed.username || parsed.password) {
    throw new Error(ctaUrlValidationMessage('mustNotIncludeCredentials'))
  }
}

export function validateCtaUrlTemplate(
  template: string,
  context: ICtaUrlInterpolationContext = CTA_URL_SAMPLE_INTERPOLATION_CONTEXT
): { ok: true } | { ok: false, code: TCtaUrlValidationErrorCode, unknownPlaceholders?: string[] } {
  const trimmed = template.trim()
  if (trimmed.length === 0) {
    return { ok: false, code: 'required' }
  }
  if (trimmed.length > 2048) {
    return { ok: false, code: 'tooLong' }
  }
  if (/^javascript:/i.test(trimmed) || /^data:/i.test(trimmed)) {
    return { ok: false, code: 'schemeNotAllowed' }
  }
  const unknown = findUnknownCtaUrlPlaceholderKeys(trimmed)
  if (unknown.length > 0) {
    return { ok: false, code: 'unknownPlaceholders', unknownPlaceholders: unknown }
  }
  if (!/^https:\/\//i.test(trimmed)) {
    return { ok: false, code: 'mustStartWithHttps' }
  }
  try {
    const expanded = substituteCtaUrlTemplate(trimmed, context)
    assertValidCtaUrlAfterSubstitution(expanded)
  }
  catch (e) {
    if (e instanceof Error) {
      if (e.message === ctaUrlValidationMessage('invalidAfterSubstitution')) {
        return { ok: false, code: 'invalidAfterSubstitution' }
      }
      if (e.message === ctaUrlValidationMessage('mustUseHttpsAfterSubstitution')) {
        return { ok: false, code: 'mustUseHttpsAfterSubstitution' }
      }
      if (e.message === ctaUrlValidationMessage('mustNotIncludeCredentials')) {
        return { ok: false, code: 'mustNotIncludeCredentials' }
      }
    }
    return { ok: false, code: 'invalidAfterSubstitution' }
  }
  return { ok: true }
}
